import { Flex, Statistic } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-layout';
import classNames from 'classnames';
import { formatDateDifference } from 'shared/utils/time/format-date-with-difference';
import { formatDate } from 'shared/utils/time/format-date.ts';
import { isExpiredDate } from 'shared/utils/time/is-expired-date.ts';
import { InvoiceStatus } from 'types/invoice-status';
import { StatisticMoney } from 'components/common/statistic-money/statistic-money';
import { InfaktSyncButton } from 'components/invoices/infakt-sync-button';
import { InvoiceEdit } from 'components/invoices/invoice-edit';
import { InvoiceFactoring } from 'components/invoices/invoice-factoring';
import { InvoiceGeneration } from 'components/invoices/invoice-generation';
import { InvoiceRelatedLinks } from 'components/invoices/invoice-related-links';
import { InvoiceStatusEdit } from 'components/invoices/invoice-status-edit';
import { InvoiceStatusTag } from 'components/invoices/invoice-status-tag';
import styles from './invoice-page-header.module.scss';
import type { IInvoicePageHeaderProps } from './types';

export const InvoicePageHeader = (props: IInvoicePageHeaderProps) => {
  const { invoice } = props;

  const renderEndDate = () => {
    if (!invoice.endPaymentPeriod || invoice.status === InvoiceStatus.Cancelled) return null;

    const { isPreExpired, isExpired } = isExpiredDate(invoice.endPaymentPeriod);

    const isCompleted = invoice.status === InvoiceStatus.Completed;
    const elementClasses = classNames(styles.dateStat, {
      [styles.warning]: isPreExpired && !isCompleted,
      [styles.expired]: isExpired && !isCompleted,
    });

    const value = isCompleted ? formatDate(invoice.endPaymentPeriod) : formatDateDifference(invoice.endPaymentPeriod);

    return <Statistic className={elementClasses} title='End date' value={value} />;
  };

  return (
    <PageHeader
      title={
        <Flex gap='small'>
          {invoice.main ? <ExclamationCircleOutlined /> : null}
          {`Invoice #${invoice.id} `}
          <InvoiceRelatedLinks order={invoice.order} trip={invoice.trip} warehouseTask={invoice.warehouseTask} />
        </Flex>
      }
      extra={[
        <InfaktSyncButton key='sync-infakt' invoiceId={invoice.id} />,
        <InvoiceStatusEdit key='invoice-status-edit' invoiceId={invoice.id} currentStatus={invoice.status} />,
        <InvoiceGeneration key='invoice-generation' invoiceId={invoice.id} />,
        ...(invoice.client.factoringEnabled ? [<InvoiceFactoring key='factoring' invoice={invoice} />] : []),
        <InvoiceEdit key='invoice-edit' invoice={invoice} />,
      ]}
      tags={[
        <InvoiceStatusTag key='status' status={invoice.status} partiallyPaidAmount={invoice.partiallyPaidAmount} />,
      ]}
      onBack={() => history.back()}>
      <div className={styles.statisticsBox}>
        {invoice.status !== InvoiceStatus.Cancelled ? (
          <Flex gap='large'>
            {invoice.startPaymentPeriod && (
              <Statistic
                className={styles.dateStat}
                title='Start date'
                value={formatDate(invoice.startPaymentPeriod)}
              />
            )}
            {renderEndDate()}
            {invoice.completedDate && invoice.endPaymentPeriod && (
              <Statistic
                className={styles.dateStat}
                title='Completed date'
                value={formatDateDifference(invoice.endPaymentPeriod, invoice.completedDate)}
              />
            )}
          </Flex>
        ) : null}
        <Flex gap='large' className={styles.statistics}>
          <StatisticMoney
            title='Price'
            currency={invoice.currency}
            value={invoice.price}
            valueInDefault={invoice.priceInDefault}
          />
          <Statistic title='VAT' prefix='%' value={invoice.vatPercent ? invoice.vatPercent : '-'} />
          {Boolean(invoice.vatSum) && (
            <StatisticMoney
              title='VAT'
              currency={invoice.currency}
              value={invoice.vatSum}
              valueInDefault={invoice.vatSumInDefault}
            />
          )}
          <StatisticMoney
            title='Total'
            currency={invoice.currency}
            value={invoice.totalSum}
            valueInDefault={invoice.totalSumInDefault}
          />
          {invoice.status === InvoiceStatus.PartiallyPaid && (
            <div className={styles.partiallyPaidStat}>
              <StatisticMoney
                title='Partially paid'
                currency={invoice.currency}
                value={invoice.partiallyPaidAmount}
                valueInDefault={invoice.partiallyPaidAmountInDefault}
              />
            </div>
          )}
        </Flex>
      </div>
    </PageHeader>
  );
};
